import { inject, injectable } from 'inversify';
import { reaction } from 'mobx';
import { TYPES } from '@/engine/ioc';
import { v4 as uuid } from 'uuid';

import { PointIndicationPointerHandler } from '@/components/boxes/indication-handler/PointIndicationPointerHandler.ts';

import type { IReactionDisposer } from 'mobx';
import type { IHandlerDescriptor } from '@/components/viewPort/PointerController.ts';
import type { ViewController } from '@/engine/app/View.controller.ts';
import type { IMeasurementViewModel } from '@/engine/app/interfaces.ts';
import type { ViewsService } from '@/engine/app/Views.service.ts';
import type { IIndicationController, IIndicationStateManager } from '@/components/boxes/objectBoxes/interfaces.ts';
import type { BoxIndicationStateStore } from '@/components/boxes/common/BoxIndicationStateStore/BoxIndicationStateStore.ts';
import type { VariableDistanceMeasurementBoxResolver } from '@/surgeries/cardiac/TTVR/components/boxes/measurementBoxes/VariableDistanceMeasurementBox/VariableDistanceMeasurementBox.resolver.ts';

@injectable()
export class VariableDistancesBoxController implements IIndicationStateManager {
  private readonly _viewsService: ViewsService;

  private readonly _viewController: ViewController;

  private readonly _indicationStateStore: BoxIndicationStateStore;

  private readonly _measurementBoxResolver: VariableDistanceMeasurementBoxResolver;

  private _changeStateDisposer: IReactionDisposer | undefined;

  private _indicationController: IIndicationController | undefined;

  private _handlersDescriptors: Array<IHandlerDescriptor> = [];

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.ViewController) viewController: ViewController,
    @inject(TYPES.BoxIndicationStateStore) indicationStateStore: BoxIndicationStateStore,
    @inject(TYPES.VariableDistanceMeasurementBoxResolver) resolver: VariableDistanceMeasurementBoxResolver
  ) {
    this._measurementBoxResolver = resolver;
    this._viewsService = viewsService;
    this._viewController = viewController;
    this._indicationStateStore = indicationStateStore;
  }

  private async _startIndicating(indicationController: IIndicationController, label: string): Promise<void> {
    const descriptor: string = uuid();

    await indicationController.initializeIndication(descriptor, label);

    const viewPorts = this._viewController.getRegisteredViewPorts();
    viewPorts.forEach((vpm) => {
      const handler = new PointIndicationPointerHandler(vpm, indicationController);
      const handlerDescriptor = vpm.addPointerHandler(handler);
      this._handlersDescriptors.push(handlerDescriptor);
    });
  }

  private _finishIndicating(): void {
    this._handlersDescriptors.forEach((desc) => desc.remove());
    this._handlersDescriptors = [];
  }

  public async cancelIndicating(): Promise<void> {
    await this._indicationController?.cancelIndication();
  }

  public setIndicationState(isIndicating: boolean): void {
    this._indicationStateStore.setIsIndicating(isIndicating);
  }

  public get isIndicating(): boolean {
    return this._indicationStateStore.isIndicating;
  }

  public async deleteVariableDistanceByDescriptor(descriptor: string): Promise<void> {
    await this._indicationController?.removeIndicated(descriptor);
  }

  public init(boxGroup: string): void {
    const indicationController = this._measurementBoxResolver.resolveVariableDistanceIndicationController(boxGroup);
    this._indicationController = indicationController;

    const recordLabel = this._measurementBoxResolver.resolveVariableDistanceBoxRecordLabel(boxGroup);
    this._indicationController.setStateManager(this);

    this._changeStateDisposer = reaction(
      () => this._indicationStateStore.isIndicating,
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
      async (isIndicating: boolean) => {
        if (isIndicating) {
          await this._startIndicating(indicationController, recordLabel);
        } else {
          this._finishIndicating();
        }
      }
    );
  }

  public async dispose(): Promise<void> {
    this._changeStateDisposer?.();
    if (this._indicationStateStore.isIndicating) {
      await this._indicationController?.cancelIndication();
    }
    this._finishIndicating();
  }

  public setLabel(descriptor: string, label: string): void {
    this._viewsService.views.forEach((view) => {
      const vM: IMeasurementViewModel | undefined = view.getMeasurementByDescriptor(descriptor);
      vM?.setLabel(label);
    });
  }
}
